import { API_ENDPOINTS } from "../../enums/apiUrls";
import { getBearerToken } from "./bearerToken";

interface CreateSecretPayload {
  secret_id: string;
  secret_value: string;
}

interface CreateSecretResponse {
  status: string;
  secret_id: string;
  message: string;
}

/**
 * Creates a new portal secret for storing sensitive credentials.
 *
 * @param payload - The secret payload
 * @param payload.secret_id - Unique identifier for the secret
 * @param payload.secret_value - The sensitive value to store
 * @returns The created secret details including status and confirmation message
 * @throws {Error} When bearer token is unavailable or API request fails
 *
 * @example
 * const result = await createPortalSecret({
 *   secret_id: "smtp-password",
 *   secret_value: "my-secure-password"
 * });
 */
export const createPortalSecret = async (
  payload: CreateSecretPayload,
): Promise<CreateSecretResponse> => {
  const token = await getBearerToken();

  if (!token) {
    throw new Error("Bearer token not available");
  }

  const response = await fetch(API_ENDPOINTS.CREATE_PORTAL_SECRET, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      `Failed to create secret: ${errorData.message || response.statusText}`,
    );
  }

  return response.json();
};
